import { Link } from "react-router-dom";
import {
  HardHat,
  ClipboardCheck,
  UserCheck,
  FileSearch,
  Layers,
  GraduationCap,
  Building2,
  Factory,
  Wrench,
  Bot,
  Server,
  BatteryCharging,
  UtensilsCrossed,
  Zap,
  MapPin,
  ArrowRight,
} from "lucide-react";
import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { PageMeta, Reveal, SectionHead } from "@/components/shared";
import Ribbon from "@/components/Ribbon";

const heroLine = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: 0,
    transition: { duration: 0.85, delay: 0.18 + i * 0.13, ease: [0.22, 1, 0.36, 1] },
  }),
};

const CAPABILITIES = [
  {
    icon: HardHat,
    title: "Site HSE Leadership That Keeps Projects Moving",
    desc: "Experienced HSE leadership on site and across projects — so compliance is maintained, standards hold and work keeps moving.",
  },
  {
    icon: ClipboardCheck,
    title: "Risk Management for High-Risk Activities",
    desc: "Review-ready risk assessments, method statements and permit controls that protect people and programme alike.",
  },
  {
    icon: UserCheck,
    title: "Contractor Assurance You Can Rely On",
    desc: "Contractor evaluation, onboarding and monitoring — confidence that everyone on site works to the same standard.",
  },
  {
    icon: FileSearch,
    title: "Audits & Inspections That Catch Issues Early",
    desc: "Independent inspections and audits with tracked corrective actions — issues found before they become incidents.",
  },
  {
    icon: Layers,
    title: "HSE Management Systems Teams Actually Use",
    desc: "Policies and systems aligned to ISO 9001 and ISO 14001 — built for real operations, ready for client audits.",
  },
  {
    icon: GraduationCap,
    title: "Incident Investigation That Stops Repeat Issues",
    desc: "Root-cause investigation, inductions and training that turn incidents into improvement and protect your KPIs.",
  },
];

const FEATURED_SECTORS = [
  { icon: Server, name: "Data Centres & Mission Critical" },
  { icon: BatteryCharging, name: "EV Battery Manufacturing" },
  { icon: Building2, name: "Construction & Civil Engineering" },
  { icon: Wrench, name: "Manufacturing" },
];

const ALSO_SECTORS = [
  { icon: Factory, name: "Industrial & Process" },
  { icon: Bot, name: "Automation & Robotics" },
  { icon: UtensilsCrossed, name: "Food & Beverage" },
  { icon: Zap, name: "Energy & Renewables" },
];

const WHY = [
  {
    title: "Practical HSE Expertise",
    desc: "Site-focused support from experienced HSE professionals who understand how work is actually carried out.",
  },
  {
    title: "Multi-Sector Experience",
    desc: "Construction, industrial, manufacturing, automation and mission-critical environments.",
  },
  {
    title: "Risk-Based Approach",
    desc: "Effort directed where risk is highest, proportionate to the activity and the organisation.",
  },
  {
    title: "Contractor & Project Assurance",
    desc: "Confidence that contractors and projects are working to agreed HSE standards.",
  },
  {
    title: "Programme-Aware HSE Delivery",
    desc: "HSE delivery that understands programme pressures, sequencing and commercial realities.",
  },
];

const LOCATIONS = [
  {
    country: "United Kingdom",
    line: "Manchester, United Kingdom",
    note: "UK-based consultancy and principal operating market",
  },
  {
    country: "Sweden",
    line: "Supporting clients and projects across Sweden",
    note: "HSE consultancy for Swedish and Nordic projects",
  },
  {
    country: "Portugal",
    line: "Supporting clients and projects across Portugal",
    note: "HSE consultancy for Portuguese projects and markets",
  },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const logoY = useTransform(scrollYProgress, [0, 1], [0, 80]);
  return (
    <>
      <PageMeta
        title="HSE & SHEQ Consultancy | UK, Sweden & Portugal"
        description="Practical HSE and SHEQ consultancy for data centres, battery manufacturing and complex construction projects across the UK, Sweden and Portugal."
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "SafetySymbiosis",
            url: "https://www.safetysymbiosis.com",
            logo: "https://www.safetysymbiosis.com/safetysymbiosis-logo.png",
            areaServed: ["United Kingdom", "Sweden", "Portugal"],
          }),
        }}
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-white" ref={heroRef}>
        <div className="dotgrid absolute inset-y-0 right-0 hidden w-1/2 lg:block" aria-hidden="true" />
        <div className="wrap relative grid items-center gap-12 py-14 lg:grid-cols-2 lg:gap-16 lg:py-24">
          <div>
            <img
              src="/safetysymbiosis-logo.png"
              alt="SafetySymbiosis — Environment | Health | Safety"
              className="h-16 w-auto lg:hidden"
              width={1640}
              height={959}
              data-testid="hero-logo-mobile"
            />
            <motion.span
              className="eyebrow mt-6 inline-block lg:mt-0"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.05 }}
            >
              Environment | Health | Safety
            </motion.span>
            <h1 className="mt-5 font-heading text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-[3.4rem] lg:leading-[1.12]">
              {[
                <span key="l1">
                  <span className="text-brand-green">HSE leadership</span> for
                </span>,
                <span key="l2">data centre, battery</span>,
                <span key="l3">and complex construction</span>,
                <span key="l4">projects</span>,
              ].map((line, i) => (
                <span key={i} className="block overflow-hidden pb-1">
                  <motion.span
                    className="block"
                    variants={heroLine}
                    custom={i}
                    initial="hidden"
                    animate="show"
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.9, ease: "easeOut" }}
            >
              <p className="mt-6 max-w-xl text-base leading-relaxed text-brand-charcoal/85 sm:text-lg">
                SafetySymbiosis keeps high-risk projects safe, compliant and on
                programme — practical risk controls, contractor assurance and
                experienced site leadership across construction, manufacturing,
                automation, industrial, data centre and EV battery
                environments.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <Link to="/contact" className="btn-green" data-testid="hero-cta-discuss-requirements">
                  Discuss Your HSE Requirements
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/services" className="btn-outline" data-testid="hero-cta-explore-services">
                  Explore Our Services
                </Link>
              </div>
              <p className="mt-7 text-xs font-semibold uppercase tracking-[0.16em] text-brand-charcoal/60">
                Established 2014 · UK | Sweden | Portugal
              </p>
            </motion.div>
          </div>

          <motion.div
            style={{ y: logoY }}
            className="relative hidden lg:block"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.55, ease: "easeOut" }}
          >
            <div className="relative">
              <div className="card relative z-10 p-10 shadow-xl shadow-brand-navy/5">
                <img
                  src="/safetysymbiosis-logo.png"
                  alt="SafetySymbiosis — Environment | Health | Safety"
                  className="w-full max-w-md object-contain"
                  width={1640}
                  height={959}
                  data-testid="hero-logo"
                />
              </div>
              <motion.div
                className="card absolute -left-6 top-8 z-20 px-4 py-3 shadow-md"
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 1.05 }}
              >
                <p className="text-[11px] font-bold uppercase tracking-wider text-brand-greendeep">
                  Established 2014
                </p>
                <p className="text-xs text-brand-charcoal/70">UK registered consultancy</p>
              </motion.div>
              <motion.div
                className="card absolute -bottom-6 right-6 z-20 px-4 py-3 shadow-md"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 1.2 }}
              >
                <p className="text-[11px] font-bold uppercase tracking-wider text-brand-navy">
                  UK | Sweden | Portugal
                </p>
                <p className="text-xs text-brand-charcoal/70">European reach</p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="bg-brand-navy" data-testid="trust-stats-strip">
        <div className="wrap grid grid-cols-2 gap-y-8 py-10 text-white md:grid-cols-4 md:py-12">
          {[
            { v: "2014", l: "Established" },
            { v: "27+", l: "Years Professional HSE Experience" },
            { v: "Multi-Sector", l: "Experience" },
            { v: "UK | Sweden | Portugal", l: "European Markets" },
          ].map((s, i) => (
            <Reveal key={s.l} delay={i * 0.06} className="md:border-l md:border-white/10 md:pl-8 md:first:border-l-0 md:first:pl-0">
              <p className="font-heading text-2xl font-extrabold tracking-tight md:text-3xl" data-testid={`stat-${i}`}>
                {s.v}
              </p>
              <p className="mt-1.5 text-xs font-medium uppercase tracking-[0.14em] text-white/60">
                {s.l}
              </p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Introduction */}
      <section className="bg-white py-16 lg:py-24">
        <div className="wrap grid gap-8 lg:grid-cols-[1fr_1.4fr] lg:gap-16">
          <SectionHead eyebrow="Who We Are" title="Safety, Health & Environment — Working Together" />
          <Reveal delay={0.1} className="self-center">
            <p className="text-base leading-relaxed text-brand-charcoal/85 sm:text-lg">
              At SafetySymbiosis, we believe effective HSE management is about
              more than compliance. It is about understanding how work is
              actually carried out, controlling risk at source, engaging people
              and helping organisations deliver safely, efficiently and
              responsibly.
            </p>
          </Reveal>
        </div>
      </section>

      {/* What we do */}
      <section className="bg-brand-mist py-16 lg:py-24">
        <div className="wrap">
          <SectionHead
            eyebrow="Our Capabilities"
            title="What We Do"
            intro="Practical, proportionate HSE support across the full project and operational lifecycle."
          />
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {CAPABILITIES.map((c, i) => (
              <Reveal key={c.title} delay={(i % 3) * 0.07}>
                <div className="card group h-full p-7 transition-all duration-200 hover:-translate-y-1 hover:border-brand-green/40 hover:shadow-lg hover:shadow-brand-navy/5">
                  <div className="inline-flex rounded-lg bg-brand-green/10 p-3 text-brand-green transition-colors duration-200 group-hover:bg-brand-green group-hover:text-white">
                    <c.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-5 font-heading text-lg font-bold leading-snug">{c.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-brand-charcoal/80">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-10 text-center">
            <Link to="/services" className="btn-navy" data-testid="home-cta-view-all-services">
              View All Services
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Sectors */}
      <section className="bg-white py-16 lg:py-24">
        <div className="wrap">
          <SectionHead
            eyebrow="Sectors"
            title="Where Our Expertise Applies"
            intro="Multi-sector HSE experience across construction, industrial, manufacturing and mission-critical environments."
          />
          <div className="mt-12 grid gap-5 sm:grid-cols-2">
            {FEATURED_SECTORS.map((s, i) => (
              <Reveal key={s.name} delay={(i % 2) * 0.06}>
                <Link
                  to="/sectors"
                  className="card flex h-full flex-col gap-4 p-8 transition-all duration-200 hover:border-brand-green/40 hover:shadow-md"
                  data-testid={`home-sector-${i}`}
                >
                  <s.icon className="h-7 w-7 text-brand-green" />
                  <span className="font-heading text-lg font-bold leading-snug text-brand-navy">
                    {s.name}
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12">
            <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-brand-navy">
              Also experienced in
            </h3>
            <div className="mt-4 flex flex-wrap gap-3">
              {ALSO_SECTORS.map((s, i) => (
                <Link
                  key={s.name}
                  to="/sectors"
                  className="card inline-flex items-center gap-2 px-4 py-2.5 transition-all duration-200 hover:border-brand-green/40 hover:shadow-md"
                  data-testid={`home-also-sector-${i}`}
                >
                  <s.icon className="h-4 w-4 text-brand-green" />
                  <span className="font-heading text-sm font-bold leading-snug text-brand-navy">
                    {s.name}
                  </span>
                </Link>
              ))}
            </div>
          </Reveal>
          <Reveal className="mt-8 max-w-3xl">
            <p className="text-sm leading-relaxed text-brand-charcoal/70">
              Additional experience includes warehousing &amp; distribution,
              healthcare, commercial and residential projects, hospitality and
              public-sector environments.{" "}
              <Link to="/sectors" className="font-semibold text-brand-green hover:underline" data-testid="home-link-all-sectors">
                See all sectors
              </Link>
            </p>
          </Reveal>
        </div>
      </section>

      {/* Environments ribbon */}
      <section className="bg-brand-mist py-16 lg:py-20">
        <div className="wrap">
          <SectionHead
            eyebrow="Selected Professional & Project Environments"
            title="Experience Across Organisations You Will Recognise"
            intro="Experience across major construction, engineering, manufacturing, industrial and mission-critical environments."
          />
        </div>
        <Reveal className="mt-10">
          <Ribbon />
        </Reveal>
        <div className="wrap">
          <p className="mt-6 text-xs leading-relaxed text-brand-charcoal/60">
            Organisations shown reflect selected professional and project
            environments of our leadership team. They are not presented as
            direct clients of SafetySymbiosis.
          </p>
        </div>
      </section>

      {/* Why */}
      <section className="bg-white py-16 lg:py-24">
        <div className="wrap grid gap-12 lg:grid-cols-[1fr_1.3fr] lg:gap-20">
          <div>
            <SectionHead
              eyebrow="Why SafetySymbiosis"
              title="Why SafetySymbiosis?"
              intro="An established consultancy with deep professional experience behind it — practical, reliable and easy to work with."
            />
            <Reveal delay={0.15} className="mt-8">
              <Link to="/contact" className="btn-green" data-testid="why-cta-contact">
                Discuss Your HSE Requirements
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
          <div>
            {WHY.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.05}>
                <div className="flex gap-6 border-b border-brand-line py-6 first:pt-0 last:border-b-0">
                  <span className="font-heading text-sm font-extrabold text-brand-green">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="font-heading text-lg font-bold">{w.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-brand-charcoal/80">{w.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* European presence */}
      <section className="bg-brand-mist py-16 lg:py-24">
        <div className="wrap">
          <SectionHead
            eyebrow="European Presence"
            title="Supporting Clients Across Europe"
            intro="From our UK base, we support clients and projects across Sweden, Portugal and wider European markets."
          />
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {LOCATIONS.map((l, i) => (
              <Reveal key={l.country} delay={i * 0.07}>
                <div className="card flex h-full flex-col p-7 transition-shadow duration-300 hover:shadow-md">
                  <MapPin className="h-5 w-5 text-brand-green" />
                  <h3 className="mt-4 font-heading text-xl font-bold uppercase tracking-wide">{l.country}</h3>
                  <p className="mt-2 text-sm font-medium text-brand-navy">{l.line}</p>
                  <p className="mt-1 text-sm text-brand-charcoal/70">{l.note}</p>
                  <a
                    href={`mailto:enquiries@safetysymbiosis.com?subject=${encodeURIComponent(
                      `HSE Enquiry — ${l.country}`,
                    )}`}
                    className="btn-outline mt-6 w-full !py-2.5 text-sm"
                    data-testid={`home-contact-${l.country.toLowerCase().replace(" ", "-")}`}
                  >
                    Contact {l.country}
                  </a>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-brand-navy py-16 lg:py-20">
        <div className="wrap flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
          <Reveal>
            <h2 className="font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl">
              Discuss Your HSE Requirements
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/70 sm:text-base">
              Whether you need project-based support, site leadership,
              contractor assurance or wider SHEQ help — we would be glad to
              talk.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="flex flex-col gap-3 sm:flex-row">
            <Link to="/contact" className="btn-green" data-testid="home-final-cta-contact">
              Get in Touch
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
